// The reseller's own surface: signing up, and the admin that follows.
//
// ── Why this is its own file ─────────────────────────────────────────────────
//
// The same reasoning `AgentPortal.js` sets out, one audience further away.
// `App.js` is the staff portal and renders `notes`, `compileError` and the
// vendor's own Form 27 answers; this is a screen for somebody who has signed up
// on the internet. Sharing a component tree with that is how something internal
// gets rendered here by a prop nobody checked. They share `shared.js` — the
// palette, the service URL, the firm's wordmark — and nothing else.
//
// ── Scoping is not this file's job, and must never become it ─────────────────
//
// Every read below is scoped by the middleware off the session. There is no
// filtering here for *access* and there must not be: `?fh=` on the staff
// dashboard is the cautionary tale — it narrows the screen while the API goes
// on returning everything, which is fine for staff in their own lane and is a
// breach applied to an external audience. If this file ever looks like it needs
// a filter to decide who sees what, the endpoint is wrong.
//
// ── What it will not draw ────────────────────────────────────────────────────
//
// **A control the session cannot use.** `capabilities` comes back from `/me`,
// computed from the same map the routes ask — so a staff member does not see an
// invite button that answers 403. A screen deriving its own answer from the
// role is the second copy that comes to offer a button the route refuses.
//
// **And "powered by" is nowhere editable**, because it is not a field. The
// brand form has no input for it and the preview shows it on every page.

import { useState, useEffect, useCallback, useRef } from "react";
import { T, MIDDLEWARE_URL, LOGO_SRC } from "./shared";

const SESSION_KEY = "s32_reseller_session";

// ── The link token, read once at module load ─────────────────────────────────
//
// Read and **stripped from the URL immediately**, the way `App.js` handles
// `?token=` and `AgentPortal.js` handles `?t=`, so it stays out of the address
// bar, bookmarks and screenshots.
//
// Reading it here rather than in an effect also settles a trap that is hard to
// see otherwise: the token is single-use and StrictMode runs effects twice in
// development, so an effect reading the URL each time would redeem the token,
// redeem it again, and be told the link had expired — on a sign-in that had
// just succeeded.
function readLinkToken() {
  try {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("t") || "";
    if (!token) return "";
    const url = new URL(window.location.href);
    url.searchParams.delete("t");
    window.history.replaceState({}, "", url.toString());
    return token;
  } catch (e) {
    return "";
  }
}

const LINK_TOKEN = typeof window === "undefined" ? "" : readLinkToken();

// Shared between however many times the effect runs. A promise rather than a
// boolean, so a second caller waits for the first one's answer instead of
// racing past it into a signed-out state.
let redeeming = null;

const readStoredSession = () => {
  try { return localStorage.getItem(SESSION_KEY) || ""; } catch (e) { return ""; }
};
const writeStoredSession = (token) => {
  try {
    if (token) localStorage.setItem(SESSION_KEY, token);
    else localStorage.removeItem(SESSION_KEY);
  } catch (e) { /* blocked — the HttpOnly cookie may still carry the session */ }
};

// The same two-transport arrangement `agentFetch` uses, and the same reason
// `credentials: "include"` is deliberately absent: the middleware's
// `cors({origin: true})` sends no `Access-Control-Allow-Credentials`, so asking
// for the cookie cross-origin would have the browser reject every response
// rather than fall back to the header.
export function resellerFetch(path, { token, ...options } = {}) {
  return fetch(`${MIDDLEWARE_URL}${path}`, {
    ...options,
    headers: {
      ...(options.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
}

// What went wrong, in words somebody can act on. The middleware's messages are
// written for staff and for logs; a reseller reading "AGENT_PORTAL_URL is
// unset" is being told about our infrastructure.
export function signInError(status, body) {
  if (status === 429) {
    return "Too many sign-in links have been requested for that address. Please try again in a few minutes.";
  }
  if (status === 503 || status === 502) {
    return "Signing in is unavailable at the moment. Please try again shortly.";
  }
  return body?.error || "Something went wrong. Please try again.";
}

// ── Small pieces ─────────────────────────────────────────────────────────────

const Btn = ({ children, onClick, variant = "primary", disabled, type = "button", title }) => (
  <button type={type} title={title} onClick={disabled ? undefined : onClick} disabled={disabled}
    className={`rp-btn ${variant}`}>
    {children}
  </button>
);

const Field = ({ label, hint, children, problem }) => (
  <label className="rp-field">
    <span className="rp-label">{label}</span>
    {hint ? <span className="rp-hint">{hint}</span> : null}
    {children}
    {problem ? <span className="rp-bad">{problem}</span> : null}
  </label>
);

const Spinner = ({ size = 24 }) => (
  <div role="status" aria-label="Loading" className="rp-spin"
    style={{ width: size, height: size, borderWidth: Math.max(2, Math.round(size / 9)) }} />
);

// A status a reseller is in. **Pending is not a failure and must not read as
// one** — the admin is open, everything can be built, and only publishing
// waits. A red badge here would make somebody think they had done something
// wrong by signing up.
export const STATUS_WORDS = {
  pending: { label: "Being reviewed", tone: "wait" },
  approved: { label: "Approved", tone: "good" },
  suspended: { label: "Suspended", tone: "bad" },
  closed: { label: "Closed", tone: "bad" },
};

export const StatusChip = ({ status }) => {
  const said = STATUS_WORDS[status] || { label: status || "—", tone: "wait" };
  return <span className={`rp-chip ${said.tone}`}>{said.label}</span>;
};

// ── Signing up ───────────────────────────────────────────────────────────────

// The web address, checked as it is typed. **Debounced**, because it is one
// request per keystroke otherwise — and the answer is only ever *free or not*,
// never what is there.
function useSlugCheck(slug) {
  const [state, setState] = useState({ checking: false, available: null, message: "" });

  useEffect(() => {
    if (!slug || slug.length < 3) {
      setState({ checking: false, available: null, message: "" });
      return undefined;
    }
    setState(s => ({ ...s, checking: true }));
    let live = true;
    const timer = setTimeout(async () => {
      try {
        const res = await resellerFetch(`/reseller/slug-available?slug=${encodeURIComponent(slug)}`);
        const body = await res.json();
        if (!live) return;
        setState({ checking: false, available: Boolean(body.available), message: body.message || "" });
      } catch (e) {
        // A check that could not run must not read as *taken* — that would
        // block a signup over a dropped request.
        if (live) setState({ checking: false, available: null, message: "" });
      }
    }, 400);
    return () => { live = false; clearTimeout(timer); };
  }, [slug]);

  return state;
}

// The same rule the middleware applies, so the box shows what will actually be
// stored as somebody types. It is **not** the validation — that is the route's,
// and this only ever shapes what is typed into the shape it will be saved in.
export const slugify = (value) => String(value ?? "")
  .toLowerCase()
  .normalize("NFKD")
  .replace(/[̀-ͯ]/g, "")
  .replace(/[^a-z0-9]+/g, "-")
  .replace(/^-+|-+$/g, "")
  .slice(0, 40);

// ── The graphical half ───────────────────────────────────────────────────────
//
// Inline SVG at `strokeWidth="2"`, the convention `App.js` and `AgentPortal.js`
// both follow: no icon font and no sprite, because this page is opened on
// whatever somebody happens to be holding and one more host to fetch from is
// one more way it renders blank for one person once.
const Ico = ({ children }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>
);

// What a reseller gets, as three marks rather than a paragraph. It replaces the
// lead's second sentence rather than sitting beside it — the row says the same
// thing in nine words, and a page somebody is deciding on should be scannable
// before it is readable.
//
// **"Built in minutes", never "live in minutes".** Publishing waits for staff,
// which the line under the button says — a claim here that contradicted it
// would be the page arguing with itself.
const TRAITS = [
  {
    label: "Your brand",
    said: "Name, colours, logo",
    icon: (
      <>
        <path d="M12 3.5 13.9 9.1 19.5 11 13.9 12.9 12 18.5 10.1 12.9 4.5 11 10.1 9.1Z" />
        <path d="M18.6 3 19.2 4.8 21 5.4 19.2 6 18.6 7.8 18 6 16.2 5.4 18 4.8Z" />
      </>
    ),
  },
  {
    label: "Your pages",
    said: "Built in minutes",
    icon: (
      <>
        <rect x="3" y="4.5" width="18" height="15" rx="2.5" />
        <path d="M3 9.5h18M7.5 14h6" />
      </>
    ),
  },
  {
    label: "Your leads",
    said: "All in one place",
    icon: (
      <>
        <circle cx="12" cy="8.5" r="3.5" />
        <path d="M5 20a7 7 0 0 1 14 0" />
      </>
    ),
  },
];

const Tick = () => (
  <svg className="rp-tick" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m5 12.5 4.5 4.5L19 7" />
  </svg>
);

// The wordmark, in black rather than the firm's red.
//
// **Recoloured rather than a second file.** The mark is one flat `#FE5442` on
// transparent, so `brightness(0)` takes every opaque pixel to black and leaves
// the alpha alone — exactly a black wordmark, off the same asset the staff
// header, the agent portal and the contract's cover page all carry. A second
// PNG is the one that goes stale the day the mark is redrawn.
const Mark = () => <img className="rp-mark" src={LOGO_SRC} alt="Provey" />;

export function SignUp({ onSignedIn }) {
  const [form, setForm] = useState({ tradingName: "", slug: "", email: "", contactName: "" });
  const [touchedSlug, setTouchedSlug] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState({});
  const slugCheck = useSlugCheck(form.slug);

  const set = (key) => (e) => {
    const value = e.target.value;
    setForm(f => ({
      ...f,
      [key]: value,
      // The web address follows the brand name until somebody edits it. After
      // that it is theirs — a field that kept overwriting what was typed would
      // be the form arguing with the person filling it in.
      ...(key === "tradingName" && !touchedSlug ? { slug: slugify(value) } : {}),
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setProblem({});
    setBusy(true);
    try {
      const res = await resellerFetch("/reseller/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const body = await res.json();
      if (!res.ok) {
        setProblem({ [body.field || "form"]: body.error || "Could not create that account." });
        return;
      }
      writeStoredSession(body.session);
      onSignedIn(body.session);
    } catch (err) {
      setProblem({ form: "Could not reach the service. Please try again." });
    } finally {
      setBusy(false);
    }
  };

  const ready = form.tradingName.trim() && form.slug.length >= 3 && form.email.includes("@");
  const checked = form.slug.length >= 3;

  return (
    <main className="rp-narrow rp-auth">
      <Mark />
      <h1>Start your own conveyancing brand</h1>
      <p className="rp-lead">Your brand, our engine.</p>

      <ul className="rp-traits">
        {TRAITS.map(trait => (
          <li className="rp-trait" key={trait.label}>
            <Ico>{trait.icon}</Ico>
            <b>{trait.label}</b>
            <span>{trait.said}</span>
          </li>
        ))}
      </ul>

      {/* The hints are gone and the placeholders carry the examples instead.
          A label, an example and a sentence explaining the example is the same
          thing said three times on a form of four boxes. */}
      <form onSubmit={submit} className="rp-card">
        <Field label="Brand name" problem={problem.tradingName}>
          <input value={form.tradingName} onChange={set("tradingName")} autoFocus
            placeholder="Brightwater Conveyancing" />
        </Field>

        <Field label="Web address" problem={problem.slug}>
          <div className="rp-slug">
            <span className="rp-slugbase">/r/</span>
            <input value={form.slug}
              onChange={(e) => { setTouchedSlug(true); setForm(f => ({ ...f, slug: slugify(e.target.value) })); }}
              placeholder="brightwater" />
          </div>
          {checked && slugCheck.checking ? <span className="rp-checking">Checking…</span> : null}
          {checked && !slugCheck.checking && slugCheck.available === true
            ? <span className="rp-ok"><Tick />Available</span> : null}
          {checked && !slugCheck.checking && slugCheck.available === false
            ? <span className="rp-bad">{slugCheck.message || "Already in use"}</span> : null}

          {/* **The page is ahead of the service here.** Nothing serves a
              reseller's pages on their own domain today — `/r/<slug>` is the
              whole of it — so this is an offer the office keeps by hand rather
              than a switch in the admin, and it is worded as one. See the
              middleware's own note under *What is deliberately not built*. */}
          <span className="rp-aside">Prefer your own domain? You can bring one — just ask once you are set up.</span>
        </Field>

        <Field label="Email" problem={problem.email}>
          <input type="email" value={form.email} onChange={set("email")} placeholder="you@agency.com.au" />
        </Field>

        <Field label="Your name">
          <input value={form.contactName} onChange={set("contactName")} placeholder="Jo Bloggs" />
        </Field>

        {problem.form ? <p className="rp-bad">{problem.form}</p> : null}

        <Btn type="submit" disabled={!ready || busy}>
          {busy ? "Setting it up…" : "Create my brand"}
        </Btn>

        {/* Said before the press, not after it. Somebody who finds out at the
            moment they try to publish has been surprised by a rule we knew
            about the whole time. */}
        <p className="rp-fine">
          Build your pages and add your team straight away. We review every account
          before anything goes live — conveyancing is licensed work.
        </p>
      </form>

      <p className="rp-swap">
        Already have an account? <a href="/admin">Sign in</a>
      </p>
    </main>
  );
}

// ── Signing in ───────────────────────────────────────────────────────────────

export function SignIn({ onSignedIn }) {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setProblem("");
    try {
      const res = await resellerFetch("/reseller/sign-in", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) { setProblem(signInError(res.status, body)); return; }
      // The reply is the same whether or not the address is known — the
      // middleware is deliberate about that, and a screen that said "no such
      // account" would undo it.
      setSent(true);
    } catch (err) {
      setProblem("Could not reach the service. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <main className="rp-narrow rp-auth">
        <Mark />
        <h1>Check your email</h1>
        <p className="rp-lead">
          If that address can sign in, a link is on its way. It works once and
          expires in 15 minutes.
        </p>
        <p className="rp-swap">
          <button type="button" className="rp-link" onClick={() => setSent(false)}>
            Use a different address
          </button>
        </p>
      </main>
    );
  }

  return (
    <main className="rp-narrow rp-auth">
      <Mark />
      <h1>Sign in</h1>
      <p className="rp-lead">We will email you a link — there is no password.</p>
      <form onSubmit={submit} className="rp-card">
        <Field label="Email">
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus
            placeholder="you@agency.com.au" />
        </Field>
        {problem ? <p className="rp-bad">{problem}</p> : null}
        <Btn type="submit" disabled={!email.includes("@") || busy}>
          {busy ? "Sending…" : "Email me a link"}
        </Btn>
      </form>
      <p className="rp-swap">
        New here? <a href="/join">Create a brand</a>
      </p>
    </main>
  );
}

// ── The admin ────────────────────────────────────────────────────────────────

export const PANES = [
  { key: "brand", label: "Brand", needs: "brand:read" },
  { key: "pages", label: "Pages", needs: "pages:read" },
  { key: "forms", label: "Forms", needs: "forms:read" },
  { key: "leads", label: "Enquiries", needs: "leads:read" },
  { key: "team", label: "Team", needs: "users:read" },
];

export function BrandPane({ me, token, onSaved }) {
  const [draft, setDraft] = useState(() => ({ ...(me.reseller.brand || {}) }));
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const may = me.capabilities.includes("brand:write");

  const set = (key) => (e) => { setDraft(d => ({ ...d, [key]: e.target.value })); setSaved(false); };

  const save = async () => {
    setBusy(true);
    try {
      const res = await resellerFetch("/reseller/me/brand", {
        method: "POST", token,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ brand: draft }),
      });
      const body = await res.json();
      if (res.ok) { setSaved(true); onSaved(body); }
    } finally { setBusy(false); }
  };

  const colour = (key, label, hint) => (
    <Field label={label} hint={hint}>
      <div className="rp-colour">
        {/* A native colour input, which is what makes "no CSS field" bearable:
            the middleware accepts hex and nothing else, so the control that
            produces hex is the one to offer. */}
        <input type="color" value={draft[key] || me.brand[key]} onChange={set(key)} disabled={!may} />
        <input value={draft[key] || ""} onChange={set(key)} placeholder={me.brand[key]} disabled={!may} />
      </div>
    </Field>
  );

  return (
    <div className="rp-panes">
      <section className="rp-card">
        <h2>Your brand</h2>
        <Field label="Display name" hint="What appears at the top of your pages.">
          <input value={draft.displayName ?? ""} onChange={set("displayName")}
            placeholder={me.reseller.tradingName} disabled={!may} />
        </Field>
        <Field label="Tagline">
          <input value={draft.tagline ?? ""} onChange={set("tagline")} disabled={!may}
            placeholder="Conveyancing, handled." />
        </Field>
        <Field label="About you" hint="A paragraph or two. Blank lines start a new paragraph.">
          <textarea rows={5} value={draft.blurb ?? ""} onChange={set("blurb")} disabled={!may} />
        </Field>
        <Field label="Logo" hint="A link to your logo. It has to be https.">
          <input value={draft.logoUrl ?? ""} onChange={set("logoUrl")} disabled={!may}
            placeholder="https://…" />
        </Field>

        {colour("primaryColour", "Buttons and highlights")}
        {colour("textColour", "Text")}
        {colour("backgroundColour", "Background")}

        <Field label="Contact email" hint="Where your enquiries are emailed.">
          <input type="email" value={draft.contactEmail ?? ""} onChange={set("contactEmail")} disabled={!may} />
        </Field>
        <Field label="Contact phone">
          <input value={draft.contactPhone ?? ""} onChange={set("contactPhone")} disabled={!may} />
        </Field>

        {/* Warnings, never refusals — a brand's colours are the brand's. What
            this must not do is let somebody publish a page nobody can read
            without having been told. */}
        {(me.warnings || []).map(w => (
          <p key={w.field} className="rp-warn">{w.message}</p>
        ))}

        {may ? (
          <Btn onClick={save} disabled={busy}>{busy ? "Saving…" : saved ? "Saved" : "Save"}</Btn>
        ) : (
          <p className="rp-fine">Only an owner or an admin can change the brand.</p>
        )}
      </section>

      <section className="rp-preview">
        <h2>How it looks</h2>
        <div className="rp-swatch" style={{
          background: me.brand.backgroundColour, color: me.brand.textColour,
        }}>
          {me.brand.logoUrl ? <img src={me.brand.logoUrl} alt="" className="rp-swatchlogo" /> : null}
          <strong>{me.brand.displayName}</strong>
          {me.brand.tagline ? <p>{me.brand.tagline}</p> : null}
          <span className="rp-swatchbtn" style={{
            background: me.brand.primaryColour, color: me.brand.onPrimary,
          }}>Get a quote</span>
          {/* Shown because it is on every page and cannot be taken off — so it
              belongs in the preview rather than being a surprise at publish. */}
          <span className="rp-swatchfoot">{me.brand.poweredBy}</span>
        </div>
      </section>
    </div>
  );
}

export function TeamPane({ me, token, reload }) {
  const [users, setUsers] = useState(null);
  const [invite, setInvite] = useState({ email: "", role: "staff", name: "" });
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const mayInvite = me.capabilities.includes("users:invite");
  const mayRemove = me.capabilities.includes("users:remove");

  const load = useCallback(async () => {
    const res = await resellerFetch("/reseller/me/users", { token });
    const body = await res.json().catch(() => ({}));
    setUsers(body.users || []);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    setNotice("");
    try {
      const res = await resellerFetch("/reseller/me/users", {
        method: "POST", token,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(invite),
      });
      const body = await res.json();
      if (!res.ok) { setNotice(body.error || "Could not add that person."); return; }
      // Said out loud, because it is the difference between a colleague who has
      // been told and one who has not. The middleware reports it honestly and
      // this must not round it up.
      setNotice(body.invited
        ? `${body.user.email} has been emailed a sign-in link.`
        : `${body.user.email} has been added. Email is not switched on, so send them the link yourself.`);
      setInvite({ email: "", role: "staff", name: "" });
      load();
    } finally { setBusy(false); }
  };

  const remove = async (user) => {
    if (!window.confirm(`Remove ${user.name || user.email}? They will be signed out immediately.`)) return;
    const res = await resellerFetch(`/reseller/me/users/${user.userId}`, { method: "DELETE", token });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) { setNotice(body.error || "Could not remove that person."); return; }
    load();
  };

  const changeRole = async (user, role) => {
    const res = await resellerFetch(`/reseller/me/users/${user.userId}/role`, {
      method: "POST", token,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ role }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) { setNotice(body.error || "Could not change that role."); return; }
    load();
    reload();
  };

  if (!users) return <Spinner />;

  return (
    <div className="rp-panes">
      <section className="rp-card">
        <h2>Your team</h2>
        <table className="rp-table">
          <thead><tr><th>Name</th><th>Email</th><th>Role</th><th /></tr></thead>
          <tbody>
            {users.map(user => (
              <tr key={user.userId}>
                <td>{user.name || "—"}</td>
                <td className="rp-muted">{user.email}</td>
                <td>
                  {mayInvite && user.userId !== me.user.userId ? (
                    <select value={user.role} onChange={(e) => changeRole(user, e.target.value)}>
                      <option value="staff">Staff</option>
                      <option value="admin">Admin</option>
                      {me.user.role === "owner" ? <option value="owner">Owner</option> : null}
                    </select>
                  ) : <span className="rp-muted">{user.role}</span>}
                </td>
                <td>
                  {mayRemove && user.userId !== me.user.userId
                    ? <button type="button" className="rp-link" onClick={() => remove(user)}>Remove</button>
                    : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {notice ? <p className="rp-note">{notice}</p> : null}
      </section>

      {mayInvite ? (
        <section className="rp-card">
          <h2>Add somebody</h2>
          <form onSubmit={add}>
            <Field label="Their email address">
              <input type="email" value={invite.email}
                onChange={(e) => setInvite(i => ({ ...i, email: e.target.value }))} />
            </Field>
            <Field label="Their name">
              <input value={invite.name} onChange={(e) => setInvite(i => ({ ...i, name: e.target.value }))} />
            </Field>
            <Field label="What they can do"
              hint="Staff see and work enquiries. Admins can also change the brand, the pages and the team.">
              <select value={invite.role} onChange={(e) => setInvite(i => ({ ...i, role: e.target.value }))}>
                <option value="staff">Staff</option>
                <option value="admin">Admin</option>
              </select>
            </Field>
            <Btn type="submit" disabled={!invite.email.includes("@") || busy}>
              {busy ? "Adding…" : "Add them"}
            </Btn>
          </form>
        </section>
      ) : null}
    </div>
  );
}

// The question types the middleware renders as a native control. Kept in step
// with `lib/reseller/forms.js`'s own list — a type offered here that the page
// cannot draw is a question nobody can answer.
export const QUESTION_TYPES = [
  ["text", "Short answer"],
  ["longtext", "Long answer"],
  ["email", "Email address"],
  ["phone", "Phone number"],
  ["address", "Address"],
  ["number", "Number"],
  ["date", "Date"],
  ["choice", "Choose one"],
  ["checkbox", "Choose any"],
  ["consent", "Agreement"],
];

export function FormsPane({ me, token }) {
  const [forms, setForms] = useState(null);
  const [editing, setEditing] = useState(null);
  const [fields, setFields] = useState([]);
  const [busy, setBusy] = useState(false);
  const may = me.capabilities.includes("forms:write");

  const load = useCallback(async () => {
    const res = await resellerFetch("/reseller/me/forms", { token });
    const body = await res.json().catch(() => ({}));
    setForms(body.forms || []);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Which contract field a question may answer is **served, not written here**
  // — the rule `GET /contract-fields` follows. A copy of that list in this
  // bundle is one that drifts from the mapping that actually runs. It needs the
  // staff token, so a reseller sees the labels only where the deployment
  // supplies them; without it the mapping simply is not offered, and their
  // answers still reach the enquiry.
  const [mappable, setMappable] = useState([]);
  useEffect(() => {
    let live = true;
    resellerFetch("/reseller-fields", { token })
      .then(r => (r.ok ? r.json() : { fields: [] }))
      .then(b => { if (live) setMappable(b.fields || []); })
      .catch(() => {});
    return () => { live = false; };
  }, [token]);

  // The question sets the service offers to start from. **Fetched, not written
  // here**, the same rule as the mappable fields one block up: which questions
  // are safe to put on a landing page is a decision about the firm's own forms
  // — every one of them is a question its public quote form already asks — and
  // a copy in this bundle would be the one that drifts. A middleware that has
  // never heard of the route simply offers none, so the button is absent
  // rather than broken.
  const [starters, setStarters] = useState([]);
  useEffect(() => {
    let live = true;
    resellerFetch("/reseller/me/form-starters", { token })
      .then(r => (r.ok ? r.json() : { starters: [] }))
      .then(b => { if (live) setStarters(b.starters || []); })
      .catch(() => {});
    return () => { live = false; };
  }, [token]);

  const open = (form) => {
    setEditing(form || { name: "", heading: "", intro: "", submitLabel: "Send", thankYou: "" });
    setFields(form?.questions ? [...form.questions] : [{ label: "", type: "text", required: true }]);
  };

  // A starter opens as an **unsaved** form, exactly as pressing New does: it is
  // a draft to edit and press Save on, not a form that appears behind you. So
  // nothing is written until somebody has looked at the questions, and the
  // brand's own wording can go in before a client ever sees it.
  const start = (starter) => {
    const { key, questions, ...rest } = starter;
    setEditing(rest);
    setFields(questions ? [...questions] : []);
  };

  const save = async () => {
    setBusy(true);
    try {
      const res = await resellerFetch("/reseller/me/forms", {
        method: "POST", token,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...editing, questions: fields }),
      });
      if (res.ok) { setEditing(null); load(); }
    } finally { setBusy(false); }
  };

  const setField = (index, patch) =>
    setFields(list => list.map((f, i) => (i === index ? { ...f, ...patch } : f)));

  if (!forms) return <Spinner />;

  if (editing) {
    return (
      <section className="rp-card">
        <h2>{editing.formId ? "Edit form" : "New form"}</h2>
        <Field label="What this form is called" hint="Only you see this.">
          <input value={editing.name} onChange={(e) => setEditing(f => ({ ...f, name: e.target.value }))} />
        </Field>
        <Field label="Heading on the form">
          <input value={editing.heading} onChange={(e) => setEditing(f => ({ ...f, heading: e.target.value }))} />
        </Field>
        <Field label="What the button says">
          <input value={editing.submitLabel}
            onChange={(e) => setEditing(f => ({ ...f, submitLabel: e.target.value }))} />
        </Field>
        <Field label="What they see after sending">
          <textarea rows={3} value={editing.thankYou}
            onChange={(e) => setEditing(f => ({ ...f, thankYou: e.target.value }))} />
        </Field>

        <h3>Questions</h3>
        {fields.map((field, index) => (
          <div className="rp-question" key={index}>
            <input className="rp-qlabel" value={field.label} placeholder="What are you asking?"
              onChange={(e) => setField(index, { label: e.target.value })} />
            <select value={field.type} onChange={(e) => setField(index, { type: e.target.value })}>
              {QUESTION_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
            {(field.type === "choice" || field.type === "checkbox") ? (
              <input className="rp-qopts" placeholder="Answers, separated by commas"
                value={(field.options || []).join(", ")}
                onChange={(e) => setField(index, {
                  options: e.target.value.split(",").map(s => s.trim()).filter(Boolean),
                })} />
            ) : null}
            {mappable.length ? (
              <select value={field.maps || ""} onChange={(e) => setField(index, { maps: e.target.value })}
                title="Fill a field on the matter with this answer">
                <option value="">Just record the answer</option>
                {mappable.map(f => <option key={f.name} value={f.name}>{f.label}</option>)}
              </select>
            ) : null}
            <label className="rp-req">
              <input type="checkbox" checked={Boolean(field.required)}
                onChange={(e) => setField(index, { required: e.target.checked })} /> Required
            </label>
            <button type="button" className="rp-link"
              onClick={() => setFields(list => list.filter((_, i) => i !== index))}>Remove</button>
          </div>
        ))}
        <Btn variant="ghost"
          onClick={() => setFields(list => [...list, { label: "", type: "text", required: false }])}>
          Add a question
        </Btn>

        <div className="rp-actions">
          <Btn onClick={save} disabled={busy || !editing.name.trim()}>
            {busy ? "Saving…" : "Save form"}
          </Btn>
          <Btn variant="ghost" onClick={() => setEditing(null)}>Cancel</Btn>
        </div>
      </section>
    );
  }

  return (
    <section className="rp-card">
      <h2>Your forms</h2>
      {forms.length === 0
        ? <p className="rp-empty">No forms yet. A form is the questions your clients answer.</p>
        : (
          <ul className="rp-list">
            {forms.map(form => (
              <li key={form.formId}>
                <div>
                  <strong>{form.name || "Untitled form"}</strong>
                  <span className="rp-muted"> · {(form.questions || []).length} questions</span>
                </div>
                {may ? <button type="button" className="rp-link" onClick={() => open(form)}>Edit</button> : null}
              </li>
            ))}
          </ul>
        )}
      <div className="rp-actions">
        {may ? <Btn onClick={() => open(null)}>New form</Btn> : null}
        {may ? starters.map(starter => (
          <Btn key={starter.key} variant="ghost" onClick={() => start(starter)}>
            Start from our {(starter.name || "starter").toLowerCase()}
          </Btn>
        )) : null}
      </div>
    </section>
  );
}

export function PagesPane({ me, token }) {
  const [pages, setPages] = useState(null);
  const [forms, setForms] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const may = me.capabilities.includes("pages:write");
  const mayPublish = me.capabilities.includes("pages:publish");

  const load = useCallback(async () => {
    const [p, f] = await Promise.all([
      resellerFetch("/reseller/me/pages", { token }).then(r => r.json()).catch(() => ({})),
      resellerFetch("/reseller/me/forms", { token }).then(r => r.json()).catch(() => ({})),
    ]);
    setPages(p.pages || []);
    setForms(f.forms || []);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    setNotice("");
    try {
      const res = await resellerFetch("/reseller/me/pages", {
        method: "POST", token,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editing),
      });
      const body = await res.json();
      if (!res.ok) { setNotice(body.error || "Could not save that page."); return; }
      setEditing(null);
      load();
    } finally { setBusy(false); }
  };

  const publish = async (page, published) => {
    const res = await resellerFetch(`/reseller/me/pages/${page.pageId}/publish`, {
      method: "POST", token,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ published }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) { setNotice(body.error || "Could not change that."); return; }
    setNotice("");
    load();
  };

  if (!pages) return <Spinner />;

  if (editing) {
    return (
      <section className="rp-card">
        <h2>{editing.pageId ? "Edit page" : "New page"}</h2>
        <Field label="Page name" hint="Only you see this.">
          <input value={editing.name || ""} onChange={(e) => setEditing(p => ({ ...p, name: e.target.value }))} />
        </Field>
        <Field label="Web address" hint={`It will live at /r/${me.reseller.slug}/…`}>
          <input value={editing.slug || ""}
            onChange={(e) => setEditing(p => ({ ...p, slug: slugify(e.target.value) }))} placeholder="sell" />
        </Field>
        <Field label="Heading">
          <input value={editing.heading || ""}
            onChange={(e) => setEditing(p => ({ ...p, heading: e.target.value }))} />
        </Field>
        <Field label="What the page says" hint="Blank lines start a new paragraph.">
          <textarea rows={6} value={editing.body || ""}
            onChange={(e) => setEditing(p => ({ ...p, body: e.target.value }))} />
        </Field>
        <Field label="Which form goes on it">
          <select value={editing.formId || ""}
            onChange={(e) => setEditing(p => ({ ...p, formId: e.target.value }))}>
            <option value="">No form — just words</option>
            {forms.map(f => <option key={f.formId} value={f.formId}>{f.name || "Untitled"}</option>)}
          </select>
        </Field>
        <label className="rp-req">
          <input type="checkbox" checked={Boolean(editing.noindex)}
            onChange={(e) => setEditing(p => ({ ...p, noindex: e.target.checked }))} />
          {" "}Keep this page out of search engines
        </label>
        {notice ? <p className="rp-bad">{notice}</p> : null}
        <div className="rp-actions">
          <Btn onClick={save} disabled={busy || !editing.slug}>{busy ? "Saving…" : "Save page"}</Btn>
          <Btn variant="ghost" onClick={() => setEditing(null)}>Cancel</Btn>
        </div>
      </section>
    );
  }

  return (
    <section className="rp-card">
      <h2>Your pages</h2>

      {/* Said where the press is, not only at signup. Somebody who comes back a
          week later has forgotten, and a Publish button that answers with an
          error is a worse way to be reminded. */}
      {!me.canPublish ? (
        <p className="rp-note">
          Your account is still being reviewed, so pages cannot go live yet. You can
          build them and preview them now.
        </p>
      ) : null}

      {pages.length === 0
        ? <p className="rp-empty">No pages yet. A page is where your clients land.</p>
        : (
          <ul className="rp-list">
            {pages.map(page => (
              <li key={page.pageId}>
                <div>
                  <strong>{page.name || page.heading || page.slug}</strong>
                  <span className="rp-muted"> · /r/{me.reseller.slug}/{page.slug}</span>
                  {page.published ? <span className="rp-chip good">Live</span> : null}
                </div>
                <div className="rp-rowacts">
                  <a className="rp-link" href={`${MIDDLEWARE_URL}/reseller/me/pages/${page.pageId}/preview`}
                    target="_blank" rel="noreferrer">Preview</a>
                  {may ? <button type="button" className="rp-link" onClick={() => setEditing(page)}>Edit</button> : null}
                  {mayPublish ? (
                    <button type="button" className="rp-link"
                      onClick={() => publish(page, !page.published)}>
                      {page.published ? "Take down" : "Publish"}
                    </button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      {notice ? <p className="rp-bad">{notice}</p> : null}
      {may ? <Btn onClick={() => setEditing({ name: "", slug: "", heading: "", body: "" })}>New page</Btn> : null}
    </section>
  );
}

export const LEAD_STATUSES = [
  ["new", "New"],
  ["contacted", "Contacted"],
  ["quoted", "Quoted"],
  ["declined", "Not proceeding"],
];

export function LeadsPane({ me, token }) {
  const [leads, setLeads] = useState(null);
  const may = me.capabilities.includes("leads:write");

  const load = useCallback(async () => {
    const res = await resellerFetch("/reseller/me/leads", { token });
    const body = await res.json().catch(() => ({}));
    setLeads(body.leads || []);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (lead, status) => {
    await resellerFetch(`/reseller/me/leads/${lead.leadId}`, {
      method: "POST", token,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    });
    load();
  };

  if (!leads) return <Spinner />;

  return (
    <section className="rp-card">
      <h2>Enquiries</h2>
      {leads.length === 0
        ? <p className="rp-empty">No enquiries yet. They appear here as soon as somebody fills in one of your forms.</p>
        : (
          <ul className="rp-list rp-leads">
            {leads.map(lead => (
              <li key={lead.leadId}>
                <div className="rp-leadhead">
                  <strong>{lead.contact.name || "Someone"}</strong>
                  {/* Converted is ours to set, so it is shown and not offered. */}
                  {lead.converted ? <span className="rp-chip good">Matter opened</span> : null}
                  {may && !lead.converted ? (
                    <select value={lead.status} onChange={(e) => setStatus(lead, e.target.value)}>
                      {LEAD_STATUSES.map(([value, label]) =>
                        <option key={value} value={value}>{label}</option>)}
                    </select>
                  ) : <span className="rp-muted">{lead.status}</span>}
                </div>
                <div className="rp-muted">
                  {[lead.contact.email, lead.contact.phone, lead.contact.address]
                    .filter(Boolean).join(" · ") || "No contact details given"}
                </div>
                <details>
                  <summary className="rp-link">What they said</summary>
                  <dl className="rp-answers">
                    {Object.entries(lead.answers || {}).map(([key, value]) => (
                      <div key={key}>
                        <dt>{key.replace(/_/g, " ")}</dt>
                        <dd>{Array.isArray(value) ? value.join(", ") : String(value)}</dd>
                      </div>
                    ))}
                  </dl>
                </details>
              </li>
            ))}
          </ul>
        )}
    </section>
  );
}

// ── The agent portal, for somebody who is also an agent ──────────────────────
//
// The mirror of the agent portal's own press, and built rather than left for
// later: a switch that only goes one way is a trapdoor, and somebody who took
// it would have to check their email to get back.
//
// **A handover, not a second sign-in.** What comes back is the agent portal's
// own one-time sign-in URL, so nothing there changes and the session it opens
// is its own. **A navigation rather than a new tab**, because the URL is only
// known after a round trip and a `window.open` that far from the press is one a
// popup blocker stops. And a failure is said out loud: being sent nowhere with
// no explanation is worse than not offering the switch.
export function SwitchToPortal({ token, portals = [] }) {
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState("");
  const other = portals.find(p => p.portal === "agent");
  if (!other) return null;

  const go = async () => {
    setBusy(true);
    setProblem("");
    try {
      const res = await resellerFetch("/reseller/me/switch", {
        token,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ portal: "agent" }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.url) {
        setBusy(false);
        setProblem(body.error || "Could not open that just now.");
        return;
      }
      window.location.assign(body.url);
    } catch (e) {
      setBusy(false);
      setProblem("Could not reach us just now.");
    }
  };

  return (
    <>
      <button type="button" className="rp-link" onClick={go} disabled={busy}>
        {busy ? "Opening…" : "Agent portal"}
      </button>
      {problem && <span className="rp-switchproblem">{problem}</span>}
    </>
  );
}

export function Admin({ token, onSignOut }) {
  const [me, setMe] = useState(null);
  const [pane, setPane] = useState("brand");
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await resellerFetch("/reseller/me", { token });
      if (!res.ok) { setFailed(true); return; }
      setMe(await res.json());
    } catch (e) { setFailed(true); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (failed) {
    onSignOut();
    return null;
  }
  if (!me) return <div className="rp-loading"><Spinner size={32} /></div>;

  // Only the panes this session can actually open. A tab that answers 403 is a
  // tab that should not have been drawn.
  const panes = PANES.filter(p => me.capabilities.includes(p.needs));
  const current = panes.some(p => p.key === pane) ? pane : (panes[0]?.key || "brand");

  return (
    <div className="rp-shell">
      <header className="rp-bar">
        <img src={LOGO_SRC} alt="" className="rp-barmark" />
        <span className="rp-brandname">{me.brand.displayName}</span>
        <StatusChip status={me.reseller.status} />
        <span className="rp-spacer" />
        <span className="rp-who">{me.user.name || me.user.email}</span>
        <SwitchToPortal token={token} portals={me.portals || []} />
        <button type="button" className="rp-link" onClick={onSignOut}>Sign out</button>
      </header>

      <nav className="rp-rail">
        {panes.map(p => (
          <button key={p.key} type="button" onClick={() => setPane(p.key)}
            aria-current={current === p.key ? "page" : undefined}>
            {p.label}
          </button>
        ))}
      </nav>

      <main className="rp-main">
        {current === "brand" && <BrandPane me={me} token={token} onSaved={load} />}
        {current === "team" && <TeamPane me={me} token={token} reload={load} />}
        {current === "forms" && <FormsPane me={me} token={token} />}
        {current === "pages" && <PagesPane me={me} token={token} />}
        {current === "leads" && <LeadsPane me={me} token={token} />}
      </main>
    </div>
  );
}

// ── The root ─────────────────────────────────────────────────────────────────

export default function ResellerPortal({ joining = false }) {
  const [token, setToken] = useState(() => readStoredSession());
  const [busy, setBusy] = useState(Boolean(LINK_TOKEN));
  const started = useRef(false);

  // Redeem the emailed link, once. Shared through a module-level promise so
  // StrictMode's double-invoke cannot spend the token twice and report the
  // second answer — which is "expired" on a sign-in that just worked.
  useEffect(() => {
    if (!LINK_TOKEN || started.current) return;
    started.current = true;
    redeeming = redeeming || resellerFetch("/reseller/session", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: LINK_TOKEN }),
    }).then(async (res) => (res.ok ? (await res.json()).session : ""));

    redeeming.then((session) => {
      if (session) { writeStoredSession(session); setToken(session); }
      setBusy(false);
    }).catch(() => setBusy(false));
  }, []);

  const signOut = useCallback(async () => {
    try { await resellerFetch("/reseller/sign-out", { method: "POST", token }); } catch (e) { /* the local session goes either way */ }
    writeStoredSession("");
    setToken("");
  }, [token]);

  const signedIn = useCallback((session) => { setToken(session); }, []);

  return (
    <>
      <style>{SHEET}</style>
      {busy ? <div className="rp-loading"><Spinner size={32} /></div>
        : token ? <Admin token={token} onSignOut={signOut} />
          : joining ? <SignUp onSignedIn={signedIn} />
            : <SignIn onSignedIn={signedIn} />}
    </>
  );
}

// ── The stylesheet ───────────────────────────────────────────────────────────
//
// The one departure from `App.js`'s inline-style convention, for the three
// things a style object has no syntax for: a hover state, `aria-current`, and a
// breakpoint. **Every colour is interpolated from `shared.js`**, so the palette
// is still the one place and this adds only the states.
const SHEET = `
.rp-shell { min-height:100vh; background:${T.bg}; display:grid;
  grid-template-columns:200px 1fr; grid-template-rows:56px 1fr;
  grid-template-areas:"bar bar" "rail main"; }
.rp-bar { grid-area:bar; display:flex; align-items:center; gap:12px; padding:0 18px;
  background:${T.white}; border-bottom:1px solid ${T.border}; }
.rp-barmark { height:22px; }
.rp-brandname { font-weight:600; font-size:14px; color:${T.text}; }
.rp-spacer { flex:1; }
.rp-who { font-size:13px; color:${T.textSoft}; }
.rp-switchproblem { font-size:12px; color:${T.red}; }
.rp-rail { grid-area:rail; background:${T.white}; border-right:1px solid ${T.border};
  padding:14px 10px; display:flex; flex-direction:column; gap:2px; }
.rp-rail button { text-align:left; padding:9px 12px; border:0; border-radius:8px;
  background:transparent; font:inherit; font-size:14px; color:${T.text}; cursor:pointer; }
.rp-rail button:hover { background:${T.bgAlt}; }
.rp-rail button[aria-current=page] { background:${T.bgAlt}; font-weight:600; }
.rp-main { grid-area:main; padding:22px; overflow:auto; }
.rp-loading { min-height:60vh; display:flex; align-items:center; justify-content:center; }

.rp-narrow { max-width:520px; margin:0 auto; padding:48px 20px 80px; }
.rp-mark { height:26px; display:block; margin:0 auto 26px; }
.rp-narrow h1 { font-size:26px; font-weight:300; letter-spacing:-0.02em; margin:0 0 8px; color:${T.text}; }
.rp-lead { margin:0 0 24px; color:${T.textSoft}; font-size:15px; line-height:1.5; }
.rp-centred { text-align:center; }

.rp-card { background:${T.white}; border:1px solid ${T.border}; border-radius:12px; padding:22px; margin-bottom:18px; }
/* ── The way in ────────────────────────────────────────────────────────────
 *
 * Signing up and signing in, scoped under '.rp-auth' so the admin keeps the
 * dense layout it wants. They are two different jobs: this is the page
 * somebody decides on, and that is a screen somebody works in — so this one
 * gets the room, the type and the single full-width press, and the rail and
 * panes behind it are untouched.
 *
 * **Black rather than ink.** T.black is in the palette already; the rest of
 * product's T.text is a dark grey, which is right for a body of dense rows
 * and reads as washed out under a 38px heading. */
.rp-auth { max-width:440px; padding:56px 24px 96px; }
.rp-auth .rp-mark { height:22px; margin:0 0 44px; filter:brightness(0); }
.rp-auth h1 { font-size:38px; line-height:1.08; font-weight:700; letter-spacing:-0.03em;
  color:${T.black}; margin:0 0 10px; }
.rp-auth .rp-lead { font-size:16px; color:${T.textSoft}; margin:0 0 34px; }

.rp-traits { list-style:none; display:flex; gap:12px; margin:0 0 30px; padding:0 0 30px;
  border-bottom:1px solid ${T.border}; }
.rp-trait { flex:1 1 0; min-width:0; }
.rp-trait svg { display:block; width:22px; height:22px; margin-bottom:11px; color:${T.black}; }
.rp-trait b { display:block; font-size:13px; font-weight:600; color:${T.black}; }
.rp-trait span { display:block; font-size:12px; color:${T.textFaint}; margin-top:2px; line-height:1.35; }

/* The card's chrome comes off: on a page of four boxes a bordered panel is a
 * frame around the only thing on the screen. */
.rp-auth .rp-card { background:transparent; border:0; border-radius:0; padding:0; margin:0; }
.rp-auth .rp-field { margin-bottom:16px; }
.rp-auth .rp-label { font-size:13px; font-weight:600; color:${T.black}; margin-bottom:6px; }
.rp-auth .rp-field input {
  padding:15px 14px; font-size:16px; border-radius:8px; border:2px solid transparent;
  background:${T.bg}; color:${T.black}; }
/* The placeholders carry the examples now the hints are gone, so they are
 * instructions rather than decoration — textFaint on the grey fill is about
 * 3:1, which is fine for a hint beside a filled box and not for the only
 * thing telling somebody what shape the answer takes. */
.rp-auth .rp-field input::placeholder { color:${T.textSoft}; }
/* The 2px border is the focus ring — it carries the whole indicator, so the
 * outline can go without taking the keyboard's only signal with it. */
.rp-auth .rp-field input:focus { outline:none; border-color:${T.black}; background:${T.white}; }

.rp-auth .rp-slug { background:${T.bg}; border:2px solid transparent; border-radius:8px;
  padding-left:14px; }
.rp-auth .rp-slug:focus-within { border-color:${T.black}; background:${T.white}; }
.rp-auth .rp-slugbase { font-size:16px; color:${T.textFaint}; padding:0; }
/* After the field rules, deliberately: equal specificity, so the later block
 * is what hands the fill and the border to the wrapper instead. */
.rp-auth .rp-slug input { background:transparent; border:0; padding-left:0; border-radius:0; }
.rp-auth .rp-slug input:focus { background:transparent; }

.rp-auth .rp-ok, .rp-auth .rp-bad, .rp-checking {
  display:flex; align-items:center; gap:5px; font-size:13px; margin-top:7px; }
.rp-checking { color:${T.textFaint}; }
.rp-tick { width:14px; height:14px; flex:0 0 auto; }
.rp-aside { display:block; font-size:12px; color:${T.textFaint}; margin-top:7px; line-height:1.45; }

.rp-auth .rp-btn { width:100%; padding:16px; font-size:16px; border-radius:8px;
  background:${T.black}; margin-top:10px; }
/* Dimming a black button gives light grey with white text on it, which reads
 * as broken rather than as not-yet. A filled grey with grey text is the same
 * thing said legibly. */
.rp-auth .rp-btn:disabled { opacity:1; background:${T.bgAlt}; color:${T.textFaint}; }

.rp-auth .rp-fine { font-size:12px; margin-top:18px; }
.rp-swap { font-size:14px; color:${T.textSoft}; margin-top:30px; }
.rp-swap a, .rp-auth .rp-link { color:${T.black}; font-weight:600; font-size:inherit;
  text-decoration:underline; text-underline-offset:3px; }

@media (max-width:520px) {
  .rp-auth { padding:36px 20px 72px; }
  .rp-auth h1 { font-size:31px; }
  .rp-traits { gap:10px; margin-bottom:30px; }
}
.rp-card h2 { font-size:16px; font-weight:600; margin:0 0 14px; color:${T.text}; }
.rp-card h3 { font-size:14px; font-weight:600; margin:20px 0 8px; color:${T.text}; }
.rp-panes { display:grid; grid-template-columns:minmax(0,1fr) 300px; gap:18px; align-items:start; }
.rp-preview { position:sticky; top:0; }

.rp-field { display:block; margin-bottom:14px; }
.rp-label { display:block; font-size:13px; font-weight:600; margin-bottom:4px; color:${T.text}; }
.rp-hint { display:block; font-size:12px; color:${T.textFaint}; margin-bottom:5px; }
.rp-field input, .rp-field textarea, .rp-field select,
.rp-question input, .rp-question select {
  width:100%; box-sizing:border-box; padding:9px 11px; font:inherit; font-size:14px;
  border:1px solid ${T.borderMid}; border-radius:8px; background:${T.white}; color:${T.text}; }
.rp-field textarea { resize:vertical; }
.rp-field input:disabled, .rp-field textarea:disabled { background:${T.bg}; color:${T.textFaint}; }

.rp-slug { display:flex; align-items:center; gap:0; }
.rp-slugbase { font-size:14px; color:${T.textFaint}; padding:9px 2px 9px 0; }
.rp-colour { display:flex; gap:8px; align-items:center; }
.rp-colour input[type=color] { width:44px; padding:2px; flex:0 0 auto; height:36px; }

.rp-btn { padding:10px 16px; font:inherit; font-size:14px; font-weight:600; border-radius:999px;
  border:1px solid transparent; cursor:pointer; background:${T.text}; color:${T.white}; }
.rp-btn.ghost { background:transparent; color:${T.text}; border-color:${T.borderMid}; }
.rp-btn:disabled { opacity:0.45; cursor:default; }
.rp-actions { display:flex; gap:10px; margin-top:16px; }
.rp-link { background:none; border:0; padding:0; font:inherit; font-size:13px;
  color:${T.text}; text-decoration:underline; cursor:pointer; }

.rp-bad { color:#c0392b; font-size:13px; display:block; margin-top:5px; }
.rp-ok { color:#1d6b3f; font-size:13px; display:block; margin-top:5px; }
.rp-warn { border:1px solid ${T.borderMid}; border-bottom-width:2px; border-radius:8px;
  padding:10px 12px; font-size:13px; color:${T.text}; margin:12px 0; background:${T.white}; }
.rp-note { background:${T.bgAlt}; border-radius:8px; padding:10px 12px; font-size:13px;
  color:${T.text}; margin:12px 0; }
.rp-fine { font-size:12px; color:${T.textFaint}; line-height:1.5; margin-top:14px; }
.rp-empty { color:${T.textFaint}; font-size:14px; margin:0 0 16px; }
.rp-muted { color:${T.textSoft}; font-size:13px; }

.rp-chip { display:inline-block; padding:2px 9px; border-radius:999px; font-size:11px;
  font-weight:600; border:1px solid ${T.borderMid}; color:${T.textSoft}; margin-left:8px; }
.rp-chip.good { color:#1d6b3f; border-color:#1d6b3f; }
.rp-chip.bad { color:#c0392b; border-color:#c0392b; }

.rp-table { width:100%; border-collapse:collapse; font-size:14px; }
.rp-table th { text-align:left; font-size:11px; text-transform:uppercase; letter-spacing:0.04em;
  color:${T.textFaint}; padding:0 8px 6px 0; font-weight:600; }
.rp-table td { padding:8px 8px 8px 0; border-top:1px solid ${T.border}; vertical-align:middle; }
.rp-table select { padding:5px 8px; font:inherit; font-size:13px;
  border:1px solid ${T.borderMid}; border-radius:6px; background:${T.white}; }

.rp-list { list-style:none; margin:0 0 16px; padding:0; }
.rp-list li { display:flex; justify-content:space-between; align-items:center; gap:12px;
  padding:11px 0; border-top:1px solid ${T.border}; }
.rp-list li:first-child { border-top:0; }
.rp-rowacts { display:flex; gap:12px; flex:0 0 auto; }
.rp-leads li { flex-direction:column; align-items:stretch; gap:4px; }
.rp-leadhead { display:flex; align-items:center; gap:10px; }
.rp-leadhead select { padding:4px 8px; font:inherit; font-size:12px; margin-left:auto;
  border:1px solid ${T.borderMid}; border-radius:6px; background:${T.white}; }
.rp-answers { margin:8px 0 0; font-size:13px; }
.rp-answers div { display:flex; gap:10px; padding:3px 0; }
.rp-answers dt { color:${T.textFaint}; min-width:150px; text-transform:capitalize; }
.rp-answers dd { margin:0; color:${T.text}; }

.rp-question { display:grid; grid-template-columns:minmax(0,2fr) 130px minmax(0,1fr) auto auto;
  gap:8px; align-items:center; padding:9px 0; border-top:1px solid ${T.border}; }
.rp-req { font-size:12px; color:${T.textSoft}; display:flex; align-items:center; gap:5px; white-space:nowrap; }
.rp-req input { width:auto; }

.rp-swatch { border:1px solid ${T.border}; border-radius:12px; padding:20px; font-size:14px; }
.rp-swatch strong { display:block; font-size:18px; margin-bottom:4px; }
.rp-swatch p { margin:0 0 14px; opacity:0.8; font-size:13px; }
.rp-swatchlogo { max-height:34px; max-width:150px; display:block; margin-bottom:12px; }
.rp-swatchbtn { display:inline-block; padding:9px 16px; border-radius:8px; font-weight:600; font-size:13px; }
.rp-swatchfoot { display:block; margin-top:18px; padding-top:12px;
  border-top:1px solid rgba(0,0,0,0.12); font-size:12px; opacity:0.7; }

@media (max-width:900px) {
  .rp-shell { grid-template-columns:1fr; grid-template-areas:"bar" "rail" "main"; grid-template-rows:56px auto 1fr; }
  .rp-rail { flex-direction:row; overflow-x:auto; border-right:0; border-bottom:1px solid ${T.border}; }
  .rp-panes { grid-template-columns:minmax(0,1fr); }
  .rp-preview { position:static; }
  .rp-question { grid-template-columns:minmax(0,1fr); }
}
`;
